import type { TreeSelectItemBase } from '@/components/Select/types';
import {
  ACCOUNT_COLUMNS,
  CONTACT_COLUMNS,
  FEEDBACK_COLUMNS,
  SOURCE_COLUMNS,
} from '@/types/filterKeyConst';
import { lowerCaseMatch } from '@/utils/util';

export const DISALLOW_SELECT_ALL_COLUMNS: string[] = [
  SOURCE_COLUMNS.SOURCE_TYPE,
  FEEDBACK_COLUMNS.REMARKS,
  ACCOUNT_COLUMNS.NAME_SEARCH,
  CONTACT_COLUMNS.NAME_SEARCH,
];

const isDisallowAllSelectColumn = (columnKey?: string) => {
  if (!columnKey) return false;
  return DISALLOW_SELECT_ALL_COLUMNS.includes(columnKey);
};

const findNode = <T extends {}>(
  list: TreeSelectItemBase<T>[],
  alias: StringNumberValue,
): TreeSelectItemBase<T> | null => {
  for (let i = 0; i < list.length; i++) {
    const el = list[i];
    if (el.alias === alias) {
      return el;
    } else {
      const target = findNode(el.childNodes || [], alias);
      if (target) return target;
    }
  }
  return null;
};

const findParentNode = <T extends {}>(
  nodes: TreeSelectItemBase<T>[],
  alias: StringNumberValue,
): TreeSelectItemBase<T> | null => {
  for (const node of nodes) {
    // 직접 자식 중에 target이 있는지 확인
    if (node.childNodes) {
      for (const child of node.childNodes) {
        if (child.alias === alias) {
          // 찾았으면 현재 노드가 부모
          return node;
        }
      }
      // 직접 자식에는 없지만, 재귀적으로 자식의 자식들을 탐색
      const parent = findParentNode(node.childNodes, alias);
      if (parent) return parent;
    }
  }
  return null;
};

const getFlattenChildNodes = <T extends {}>(
  nodes: TreeSelectItemBase<T>[],
  searchValue?: string,
): TreeSelectItemBase<T>[] => {
  const result: TreeSelectItemBase<T>[] = [];
  for (let j = 0; j < nodes.length; j++) {
    const node = nodes[j];
    if (lowerCaseMatch(node.searchValue, searchValue)) {
      result.push({
        ...node,
        childNodes: [],
      });
    }
    result.push(...getFlattenChildNodes(node.childNodes || [], searchValue));
  }
  return result;
};

// 유틸리티 함수: 재귀적으로 모든 자식 노드의 alias를 수집
const getAllChildAliases = <T extends {}>(
  nodes: TreeSelectItemBase<T>[],
): StringNumberValue[] => {
  const result: StringNumberValue[] = [];
  for (const node of nodes) {
    if (!node.checkOption?.disable) {
      result.push(node.alias);
    }
    if (node.childNodes) {
      result.push(...getAllChildAliases(node.childNodes));
    }
  }
  return result;
};

// 자식의 전체 혹은 일부가 선택되었는지 확인
const areSomeChildrenSelected = <T extends {}>(
  nodes: TreeSelectItemBase<T>[],
  selectedList: StringNumberValue[],
): boolean => {
  if (nodes.length === 0) return false;
  const childAliases = getAllChildAliases(nodes);
  const selectedCount = childAliases.filter((alias) =>
    selectedList.includes(alias),
  ).length;
  return selectedCount > 0 && selectedCount <= childAliases.length;
};

const getAllParentNode = <T extends {}>(
  nodes: TreeSelectItemBase<T>[],
  alias: StringNumberValue,
): TreeSelectItemBase<T>[] => {
  const result: TreeSelectItemBase<T>[] = [];
  let parentNode = findParentNode(nodes, alias);
  while (parentNode) {
    result.push(parentNode);
    parentNode = findParentNode(nodes, parentNode.alias);
  }

  return result;
};

const getSiblings = <T extends {}>(
  nodes: TreeSelectItemBase<T>[],
  alias: StringNumberValue,
): TreeSelectItemBase<T>[] => {
  const parentNode = findParentNode(nodes, alias);
  if (!parentNode) return [];
  return parentNode.childNodes || [];
};

const treeSelectUtils = {
  isDisallowAllSelectColumn,
  findNode,
  getFlattenChildNodes,
  getAllChildAliases,
  areSomeChildrenSelected,
  getAllParentNode,
  getSiblings,
};

export default treeSelectUtils;
